import React, { useRef, useEffect } from 'react';

interface IntroPlayerProps {
  src: string;
  startTime: number | null; // Timestamp (ms) when the intro was started by the owner
  logPrefix: string;
}

const IntroPlayer: React.FC<IntroPlayerProps> = ({ src, startTime, logPrefix }) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || startTime === null) {
      console.log(logPrefix, "IntroPlayer: No video element or start time yet.");
      return;
    }

    const currentTimeMs = Date.now();
    const elapsedMs = currentTimeMs - startTime;
    const startOffsetSeconds = Math.max(0, elapsedMs / 1000);

    console.log(logPrefix, `IntroPlayer: Setting up video. StartTime=${startTime}, Now=${currentTimeMs}, Elapsed=${elapsedMs}ms, Offset=${startOffsetSeconds}s`);

    // Ensure metadata is loaded before setting currentTime
    const handleLoadedMetadata = () => {
      if (videoElement.duration && startOffsetSeconds < videoElement.duration) {
        console.log(logPrefix, `IntroPlayer: Seeking to ${startOffsetSeconds}s`);
        videoElement.currentTime = startOffsetSeconds;
        videoElement.play().catch(e => console.error(logPrefix, 'Intro video play failed:', e));
      } else if (videoElement.duration) {
         console.log(logPrefix, `IntroPlayer: Calculated offset ${startOffsetSeconds}s is beyond video duration ${videoElement.duration}s. Not playing.`);
         // Optionally hide the player or show an alternative if the intro is already over
      } else {
         console.log(logPrefix, `IntroPlayer: Video duration not available yet.`);
      }
    };

    // If metadata is already loaded, run handler directly
    if (videoElement.readyState >= 1) { // HAVE_METADATA or higher
        handleLoadedMetadata();
    } else {
        videoElement.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
    }

    // Cleanup listener
    return () => {
      videoElement.removeEventListener('loadedmetadata', handleLoadedMetadata);
      // Optionally pause video on unmount
      // videoElement.pause();
    };

  }, [src, startTime, logPrefix]); // Re-run if src or startTime changes

  // Don't render anything if startTime isn't set yet
  if (startTime === null) {
      return <div className="bg-[#050f1a] p-4 rounded-[10px] text-white w-full mt-4">Preparing intro...</div>;
  }

  return (
    <div className="relative max-w-[480px] group">
      <video
        ref={videoRef}
        src={src}
        playsInline
        autoPlay
        muted                                      // Allow autoplay by muting
        style={{ width: '100%', height: 'auto', display: 'block' }}
        className="rounded-[10px] mb-4 bg-black"
      />
      <div className="absolute top-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded z-10">
        Intro Playing...
      </div>
    </div>
  );
};

export default IntroPlayer;
